import type { FormEvent } from "react";

import type { OtherEventFormState } from "@/types/manager";

type OtherEventDrawerProps = {
  open: boolean;
  values: OtherEventFormState;
  onChange: (field: keyof OtherEventFormState, value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving?: boolean;
};

export function OtherEventDrawer({
  open,
  values,
  onChange,
  onSubmit,
  onClose,
  saving = false,
}: OtherEventDrawerProps) {
  const overlayClasses = open ? "opacity-100" : "pointer-events-none opacity-0";
  const panelClasses = open ? "translate-x-0" : "translate-x-full";

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <div className={`fixed inset-0 z-50 transition ${open ? "visible" : "invisible"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity ${overlayClasses}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-xl transform bg-background/95 text-foreground/80 shadow-2xl transition-transform ${panelClasses}`}
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Calendar note</p>
            <h2 className="text-xl font-semibold text-foreground">Add other event</h2>
            <p className="text-sm text-muted-foreground">Capture travel, promo pushes, or other context blocks.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </header>

        <form className="flex h-[calc(100%-4rem)] flex-col gap-4 overflow-y-auto px-6 py-5" onSubmit={handleSubmit}>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Event name
            <input
              type="text"
              value={values.event_name}
              onChange={(event) => onChange("event_name", event.target.value)}
              disabled={saving}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="Promo trip, vacation, etc."
              required
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Description
            <textarea
              value={values.description}
              onChange={(event) => onChange("description", event.target.value)}
              disabled={saving}
              className="mt-2 min-h-[96px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="Outline why this block matters."
            />
          </label>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Date range</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Start date
                <input
                  type="date"
                  value={values.start_date}
                  onChange={(event) => onChange("start_date", event.target.value)}
                  disabled={saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  required
                />
              </label>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                End date
                <input
                  type="date"
                  value={values.end_date}
                  onChange={(event) => onChange("end_date", event.target.value)}
                  disabled={saving}
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
            </div>
          </div>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Notes
            <textarea
              value={values.notes}
              onChange={(event) => onChange("notes", event.target.value)}
              disabled={saving}
              className="mt-2 min-h-[96px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="Stakeholders, reminders, deal points…"
            />
          </label>
          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Keeps non-show work visible.</p>
            <button
              type="submit"
              className="rounded-lg border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-300 transition hover:border-emerald-400 disabled:opacity-60"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save event"}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
